import type {
  BodyMapReport,
  DetailsObject,
  IncidentReport,
  IncidentSeverityValues
} from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import type { TFunction } from 'i18next'
import { incidentReportsConstants } from 'locale/incidentReports'
import type { FunctionComponent } from 'react'
import { Link } from 'react-router-dom'
import { filterItems } from '.'
import { getIncidentReportSeverityLabel } from './incidentSeverityUtils'

const DetailsRow: FunctionComponent<{ details: string | undefined }> = ({
  details
}) => {
  if (details) return <Text>{details}</Text>
  return <Text>{''}</Text>
}

const getDetailsItem = (
  key: string,
  label: string,
  obj: DetailsObject | undefined
) => {
  if (!obj) return null
  if (!obj.hasBeenApplied) return null

  return {
    key,
    label: <Text>{label}</Text>,
    children: <DetailsRow details={obj.details} />
  }
}

export const getIncidentDetailsItems = (
  incidentReport: IncidentReport,
  severities: IncidentSeverityValues | undefined,
  nominative: string,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'severity',
      label: <Text>{t(incidentReportsConstants.SEVERITY)}</Text>,
      children: incidentReport.severity && (
        <Text>
          {getIncidentReportSeverityLabel(
            incidentReport.severity,
            severities,
            t
          )}
        </Text>
      )
    },
    {
      key: 'serviceUsersInvolved',
      label: (
        <Text>
          {t(incidentReportsConstants.OTHER_SERVICE_USERS_INVOLVED, {
            variable: nominative
          })}
        </Text>
      ),
      children:
        incidentReport?.case?.serviceUsersInvolved.length &&
        incidentReport?.case?.serviceUsersInvolved.length > 0 ? (
          <Text>
            {incidentReport.case.serviceUsersInvolved
              .map((serviceUser) => serviceUser.name)
              .join(', ')}
          </Text>
        ) : undefined
    },
    {
      key: 'otherPeopleInvolved',
      label: <Text>{t(incidentReportsConstants.OTHER_PEOPLE_INVOLVED)}</Text>,
      children: incidentReport.case?.otherPeopleInvolved && (
        <Text>{incidentReport.case?.otherPeopleInvolved}</Text>
      )
    },
    {
      key: 'otherWitnesses',
      label: <Text>{t(incidentReportsConstants.OTHER_WITNESS)}</Text>,
      children: incidentReport.case?.otherWitnesses && (
        <Text>{incidentReport.case?.otherWitnesses}</Text>
      )
    },
    {
      key: 'carersInvolved',
      label: <Text>{t(incidentReportsConstants.CARERES_INVOLVED)}</Text>,
      children:
        incidentReport?.case?.carersInvolved?.length &&
        incidentReport?.case?.carersInvolved.length > 0 ? (
          <Text>
            {incidentReport.case.carersInvolved
              .map((serviceUser) => serviceUser.name)
              .join(', ')}
          </Text>
        ) : undefined
    }
  ])

export const getRestrictivePracticesItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    getDetailsItem(
      'physicalIntervention',
      t(incidentReportsConstants.PHYSICAL_INTERVENTION_APPLIED),
      incidentReport.case?.physicalIntervention
    ),
    getDetailsItem(
      'restraints',
      t(incidentReportsConstants.RESTRAINTS_APPLIED),
      incidentReport.case?.restraints
    ),
    getDetailsItem(
      'chemicalRestraints',
      t(incidentReportsConstants.CHEMICAL_RESTRAINTS_APPLIED),
      incidentReport.case?.chemicalRestraints
    ),
    getDetailsItem(
      'environmentalRestraints',
      t(incidentReportsConstants.ENVIRONMENTAL_RESTRAINTS_APPLIED),
      (incidentReport.case as any)?.environmentalRestraints as
        | DetailsObject
        | undefined
    ),
    getDetailsItem(
      'verbalRestraints',
      t(incidentReportsConstants.VERBAL_RESTRAINTS_APPLIED),
      (incidentReport.case as any)?.verbalRestraints as
        | DetailsObject
        | undefined
    ),
    {
      key: 'noRestraintsDetails',
      label: (
        <Text>
          {t(incidentReportsConstants.NO_RESTRICTIVE_PRACTICES_DETAILS)}
        </Text>
      ),
      children: (incidentReport.case as any)?.noRestraintsDetails && (
        <Text>{(incidentReport.case as any).noRestraintsDetails}</Text>
      )
    }
  ])

export const getInjuriesItems = (
  incidentReport: IncidentReport,
  nominative: string,
  bodyMaps: BodyMapReport[],
  t: TFunction<string, undefined>
) => {
  const residentBodyMap = bodyMaps.find(
    (bodyMap) =>
      bodyMap.id ===
      incidentReport.case?.residentInjuries.residentInjuriesBodyMap
  )

  return filterItems([
    getDetailsItem(
      'residentInjuries',
      t(incidentReportsConstants.INJURIES_OF_RESIDENT, {
        variable: nominative
      }),
      incidentReport.case?.residentInjuries
    ),
    getDetailsItem(
      'residentInjuries.residentInjuriesBodyMap',
      t(incidentReportsConstants.RELATED_BODY),
      (incidentReport.case as any)?.residentInjuries.residentInjuriesBodyMap as
        | DetailsObject
        | undefined
    ),
    {
      key: 'residentInjuries.residentInjuriesBodyMap',
      label: <Text>{t(incidentReportsConstants.RELATED_BODY)}</Text>,
      children: incidentReport.case?.residentInjuries
        .residentInjuriesBodyMap && (
        <Link
          to={`/residents/${residentBodyMap?.resident}/charts/body_maps/${residentBodyMap?.id}`}
        >
          <Text>{residentBodyMap?.title}</Text>
        </Link>
      )
    },
    getDetailsItem(
      'carersInjuries',
      t(incidentReportsConstants.INJURIES_OF_CARERS),
      incidentReport.case?.carersInjuries
    ),
    getDetailsItem(
      'otherPartiesInjuries',
      t(incidentReportsConstants.INJURIES_OF_OTHER_PARTIES),
      incidentReport.case?.otherPartiesInjuries
    ),
    {
      key: 'noInjuriesDetails',
      label: <Text>{t(incidentReportsConstants.NO_INJURIES_DETAILS)}</Text>,
      children: (incidentReport.case as any)?.noInjuriesDetails && (
        <Text>{(incidentReport.case as any).noInjuriesDetails}</Text>
      )
    }
  ])
}

export const getDamagesItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    getDetailsItem(
      'damages',
      t(incidentReportsConstants.PROPERTIES_OR_ENV),
      incidentReport.case?.damages
    ),
    getDetailsItem(
      'damagesIntroducingRisk',
      t(incidentReportsConstants.DAMAGES_INTRODUCING_RISK_APPLIED),
      (incidentReport.case as any)?.damagesIntroducingRisk as
        | DetailsObject
        | undefined
    ),
    {
      key: 'noDamagesDetails',
      label: <Text>{t(incidentReportsConstants.NO_DAMAGES_DETAILS)}</Text>,
      children: (incidentReport.case as any)?.noDamagesDetails && (
        <Text>{(incidentReport.case as any).noDamagesDetails}</Text>
      )
    }
  ])

export const getMedicationsItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'prnMedication',
      label: <Text>{t(incidentReportsConstants.DETAILS)}</Text>,
      children: incidentReport.case?.prnMedication?.details && (
        <Text>{incidentReport.case?.prnMedication?.details}</Text>
      )
    }
  ])

export const getOpinionItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'serviceUserOpinion',
      label: <Text>{t(incidentReportsConstants.DETAILS)}</Text>,
      children: incidentReport.case?.serviceUserOpinion && (
        <Text>{incidentReport.case?.serviceUserOpinion}</Text>
      )
    }
  ])

export const getAdditionalItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) =>
  filterItems([
    {
      key: 'incidentOtherInformation',
      label: <Text>{t(incidentReportsConstants.DETAILS)}</Text>,
      children: incidentReport.case?.incidentOtherInformation && (
        <Text>{incidentReport.case?.incidentOtherInformation}</Text>
      )
    }
  ])

export const getDebriefingItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) => {
  return filterItems([
    {
      key: 'debriefingNotes',
      label: <Text>{t(incidentReportsConstants.NOTES)}</Text>,
      children:
        incidentReport.case?.debriefingNotes?.length &&
        incidentReport.case?.debriefingNotes?.length > 0 ? (
          <Text>{incidentReport.case?.debriefingNotes}</Text>
        ) : undefined
    }
  ])
}

export const getLearningAndOutcomesItems = (
  incidentReport: IncidentReport,
  t: TFunction<string, undefined>
) => {
  return filterItems([
    {
      key: 'rootCause',
      label: <Text>{t(incidentReportsConstants.ROOT_CAUSE)}</Text>,
      children: incidentReport.case?.rootCause ? (
        <Text>{incidentReport.case?.rootCause}</Text>
      ) : undefined
    },
    {
      key: 'preventativeActions',
      label: <Text>{t(incidentReportsConstants.ACTIONS_TAKEN)}</Text>,
      children: incidentReport.case?.preventativeActions ? (
        <Text>{incidentReport.case?.preventativeActions}</Text>
      ) : undefined
    },
    {
      key: 'otherNotes',
      label: <Text>{t(incidentReportsConstants.OTHER_NOTES)}</Text>,
      children: incidentReport.case?.otherNotes ? (
        <Text>{incidentReport.case?.otherNotes}</Text>
      ) : undefined
    }
  ])
}
